/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  Typography,
  useTheme,
  Box,
  Chip,
  Grid,
  TextField,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import {
  type GridSortModel,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import CustomDataGrid from 'components/customDataGrid/CustomDataGrid';
import { Fragment, useState, useEffect } from 'react';
import { colorTheme } from '../../theme';
import { useNavigate } from 'react-router-dom';
import {
  getAllConversation,
  readChatConversation,
  updateConversationStatus,
} from 'service/ChatService';
import { type ConversationAttributesType } from 'types/services';
import { useDispatch, useSelector } from 'react-redux';
import { type RootState } from 'store';
import { setUserName, setConversationId } from 'store/chat';
import { DialogBox } from 'components';
import AddChat from './AddChat';
import GroupChat from './GroupChat';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import {
  commonConstantTexts,
  chatStatusTypeOption,
} from 'constants/constantText';
import { strings } from 'i18n/Strings';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';

type GridRowData = Record<string, unknown>;

const Chats = (): JSX.Element => {
  const dispatch = useDispatch();
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<
    ConversationAttributesType[]
  >([]);
  const [filters, setFilters] = useState<unknown>({
    startDate: null,
    endDate: null,
    status: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenBulkChat, setIsModalOpenBulkChat] = useState(false);
  const { empId } = useSelector((state: RootState) => state.onboarding);
  const userId = empId;
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.chats);
  const [searchValue, setSearchValue] = useState('');
  const handleEvent = (id: number): void => {
    navigate(`/customer-chat/${id}`);
  };

  const handleChatDialogClose = (): void => {
    setIsModalOpen(false);
    setIsModalOpenBulkChat(false);
  };

  const handleExistingConversation = (userId: number): void => {
    setSearchValue(String(userId));
    dispatch(setCurrentPage({ key: 'chats', value: 0 }));
  };

  const getConversationData = (): void => {
    getAllConversation({
      page: Number(pageInfo?.page) + 1,
      size: pageInfo?.pageSize,
      sortBy: pageInfo?.sortBy,
      sortOrder: pageInfo?.sortOrder,
      searchValue,
      filters,
    })
      .then((res) => {
        if (typeof res !== 'undefined' && 'basicInfo' in res) {
          const { basicInfo } = res as {
            basicInfo: ConversationAttributesType[];
          };
          if (basicInfo) {
            setConversation(basicInfo?.rows);
            setPageCount(basicInfo?.count);
          }
        }
      })
      .catch((err: Error) => {
        console.log('Error getting data:', err);
      });
  };

  useEffect(() => {
    getConversationData();
  }, [
    userId,
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    searchValue,
  ]);

  const handleApplyFilter = async (): Promise<void> => {
    dispatch(setCurrentPage({ key: 'chats', value: 0 }));
    getConversationData();
  };

  const markAsRead = async (id: number): Promise<void> => {
    if (id) {
      await readChatConversation(id);
    }
  };

  const handleUpdateStatus = async (
    conversationId: number,
    status: string,
  ): Promise<void> => {
    if (conversationId) {
      const data = { status, userId, createdBy: empId, senderType: 'employee' };

      await updateConversationStatus(conversationId, data);
    }
    getConversationData();
  };

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const getStatus = (status: string): string => {
    switch (status) {
      case 'new':
        return strings.displayText.new;
      case 'Re-open':
        return strings.displayText.reOpenText;
      case 'Close':
        return strings.displayText.closedText;
      default:
        return status;
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'createdAt',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dateColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'userId',
      flex: 0.7,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userID}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.userId}
        </Typography>
      ),
    },
    {
      field: 'userName',
      flex: 1.2,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.nameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.userName}
        </Typography>
      ),
    },

    {
      field: 'messages',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.descriptionColumn}
        </Typography>
      ),
      flex: 2,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.messages}
        </Typography>
      ),
    },
    {
      field: 'status',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.statusColumn}
        </Typography>
      ),
      flex: 0.7,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {getStatus(row?.status)}
        </Typography>
      ),
    },
    {
      field: 'isRead',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.actionsColumn}
        </Typography>
      ),
      flex: 0.7,
      renderCell: ({ row }) => (
        <Box>
          {row?.status === 'New' ? (
            '-'
          ) : (
            <>
              {row?.status === 'Close' ? (
                <Chip
                  label={strings.displayText.reOpen}
                  variant="outlined"
                  color="error"
                  onClick={(event: { stopPropagation: () => void }) => {
                    event.stopPropagation();
                    const reopen = 'Re-open';
                    void handleUpdateStatus(row?.conversationId, reopen);
                  }}
                />
              ) : (
                <Chip
                  label={strings.displayText.closeLable}
                  variant="outlined"
                  color="success"
                  onClick={(event: { stopPropagation: () => void }) => {
                    event.stopPropagation();
                    const close = 'Close';
                    void handleUpdateStatus(row?.conversationId, close);
                  }}
                />
              )}
            </>
          )}
        </Box>
      ),
    },
  ];
  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'chats', value: 0 }));
  };
  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'chats',
        value: model[0].field,
      }),
    );
    dispatch(
      setSortOrder({
        key: 'chats',
        value: model[0].sort,
      }),
    );
    dispatch(setCurrentPage({ key: 'chats', value: 0 }));
  };
  return (
    <Fragment>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h2"
          color={colors.gray[100]}
          fontWeight="bold"
          sx={{ fontFamily: 'Inter !important' }}
        >
          {strings.displayText.chatsText}
        </Typography>
      </Box>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={5} />
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 200, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Status"
              value={filters.status}
              onChange={(e) => {
                handleFilterChange('status', e.target.value);
              }}
            >
              {chatStatusTypeOption.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={() => {
              void handleApplyFilter();
            }}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
      </Grid>

      <CustomDataGrid
        columns={columns}
        rows={conversation && (conversation as GridRowData[])}
        getRowId={(row: GridRowData) =>
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `${String(row?.conversationId)}`
        }
        onRowClick={(row) => {
          const id: number = row?.row?.userId;
          if (id) {
            void markAsRead(id);
            handleEvent(id);
            if (row) {
              dispatch(setUserName(row?.row?.userName));
              dispatch(setConversationId(row?.row?.conversationId));
            }
          }
        }}
        handleSearch={handleSearch}
        getRowClassName={(params) =>
          params?.row?.isRead === false
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        currentPage={commonConstantTexts.chatsPage}
        pageCount={pageCount}
        addButtonTitle={strings.displayText.addChat}
        onAddButtonClick={() => {
          setIsModalOpen(true);
        }}
        onAddButtonClick2={() => {
          setIsModalOpenBulkChat(true);
        }}
        addButtonTitle2={strings.displayText.bulkChat}
        searchEnabled={true}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          handleSortChange(mode);
        }}
      />
      <DialogBox
        open={isModalOpen}
        onClose={handleChatDialogClose}
        title={strings.displayText.createChatTitle}
      >
        <AddChat
          onClose={handleChatDialogClose}
          getConversationDetails={getConversationData}
          onExistingConversation={handleExistingConversation}
        />
      </DialogBox>
      <DialogBox
        open={isModalOpenBulkChat}
        onClose={handleChatDialogClose}
        title={strings.displayText.bulkChatTitle}
      >
        <GroupChat
          onClose={handleChatDialogClose}
          getConversationDetails={getConversationData}
        />
      </DialogBox>
    </Fragment>
  );
};

export default Chats;
